/**
 * Post Summary – Frontend View Script
 *
 * Handles expand/collapse behaviour for the summary box.
 * Loaded by WordPress via block.json → viewScript.
 */

import { __ } from "@wordpress/i18n";

// Fallbacks — overridden by data attributes set from the block's
// settings or Settings → Aptum AI Summary.
const COLLAPSED_ITEMS = 2;
const LABEL_MORE = __("Show more", "aptum-ai-post-summarizer");
const LABEL_LESS = __("Show less", "aptum-ai-post-summarizer");

function initPostSummary(container: HTMLElement): void {
	// 0 is valid (fully collapsed), so only fall back when missing/invalid.
	const parsedItems = parseInt(container.dataset.collapsedItems ?? "", 10);
	const collapsedItems =
		Number.isNaN(parsedItems) || parsedItems < 0
			? COLLAPSED_ITEMS
			: parsedItems;
	const labelMore = container.dataset.labelMore || LABEL_MORE;
	const labelLess = container.dataset.labelLess || LABEL_LESS;

	const content = container.querySelector<HTMLElement>(
		".wp-block-aptum-post-summary__content",
	);
	const toggle = container.querySelector<HTMLButtonElement>(
		".wp-block-aptum-post-summary__toggle",
	);

	if (!content || !toggle) return;

	const items = content.querySelectorAll("li");

	// If there are fewer items than the threshold, hide the toggle entirely.
	if (items.length <= collapsedItems) {
		toggle.style.display = "none";
		container.removeAttribute("data-collapsed");
		content.style.maxHeight = "none";
		return;
	}

	// Distance from the top of the content box to the bottom of the last
	// visible item, so any spacing above the list is included — and with
	// 0 visible items the box collapses completely.
	function measureCollapsed(): number {
		if (collapsedItems === 0) return 0;

		const last = items[collapsedItems - 1];
		return (
			last.getBoundingClientRect().bottom -
			content!.getBoundingClientRect().top +
			parseFloat(getComputedStyle(last).marginBottom)
		);
	}

	function measureExpanded(): number {
		return content!.scrollHeight;
	}

	function applyState(): void {
		const isCollapsed = container.getAttribute("data-collapsed") === "true";

		if (isCollapsed) {
			content!.style.maxHeight = measureCollapsed() + "px";
			toggle!.textContent = labelMore;
		} else {
			content!.style.maxHeight = measureExpanded() + "px";
			toggle!.textContent = labelLess;
		}
	}

	// Initial state: collapsed.
	container.setAttribute("data-collapsed", "true");
	applyState();

	toggle.addEventListener("click", () => {
		const isCollapsed = container.getAttribute("data-collapsed") === "true";
		container.setAttribute(
			"data-collapsed",
			isCollapsed ? "false" : "true",
		);
		applyState();
	});

	// Re-measure on resize to keep collapsed height accurate.
	let resizeTimer: ReturnType<typeof setTimeout>;
	window.addEventListener("resize", () => {
		clearTimeout(resizeTimer);
		resizeTimer = setTimeout(applyState, 150);
	});
}

// ─── Boot ────────────────────────────────────────────────────────────

function boot(): void {
	document
		.querySelectorAll<HTMLElement>(".wp-block-aptum-post-summary")
		.forEach(initPostSummary);
}

if (document.readyState === "loading") {
	document.addEventListener("DOMContentLoaded", boot);
} else {
	boot();
}
